import { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye } from 'lucide-react';
import { BlockLoader } from '../../components/loading';
import LmsSlidePreviewFrame from '../../components/LmsSlidePreviewFrame';
import { lmsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { previewSlides, type LmsPreviewSlide } from '../../utils/lmsSlidePreview';

/** Router state for /lms/content/:id/preview. The editor's Preview button sends `from: 'editor'`, so Back returns there. */
export interface LmsModulePreviewLocationState {
  from?: 'editor';
}

const statusConfig: Record<string, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-yellow-100 text-yellow-700' },
  published: { label: 'Published', className: 'bg-green-100 text-green-700' },
  archived: { label: 'Archived', className: 'bg-gray-100 text-gray-700' },
};

// Admin-only preview of a module (Grow > Content Modules > Preview, and the editor's
// Preview button).
//
// A slide module is shown one slide at a time in a phone-sized frame, the way the
// learner's app shows it, with each quiz's answer key marked. It only READS the module
// (GET /lms/admin/modules/:id): no progress, points, timer or quiz attempt is recorded.
//
// An imported SCORM package keeps its own player, unchanged: learners consume SCORM on
// the mobile app, and this lets an admin verify a package plays.
/** "2 min 30 s", "45 s", "5 min". */
const duration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m === 0) return `${s} s`;
  return s === 0 ? `${m} min` : `${m} min ${s} s`;
};

/** "minimum time 2 min 30 s · quiz timer 5 min", or null when the module sets neither. */
export const learnerTiming = (minTimeSeconds: unknown, quizMinutes: unknown): string | null => {
  const parts: string[] = [];
  const min = Number(minTimeSeconds);
  if (Number.isFinite(min) && min > 0) parts.push(`minimum time ${duration(min)}`);
  const quiz = Number(quizMinutes);
  if (Number.isFinite(quiz) && quiz > 0) parts.push(`quiz timer ${duration(quiz * 60)}`);
  return parts.length > 0 ? parts.join(' · ') : null;
};

export default function LmsModulePreviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const projectId = useAuthStore((s) => s.selectedProjectId) ?? undefined;
  const [loading, setLoading] = useState(true);
  const [launchUrl, setLaunchUrl] = useState<string | null>(null);
  const [moduleType, setModuleType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [moduleTitle, setModuleTitle] = useState('');
  const [moduleStatus, setModuleStatus] = useState<string | null>(null);
  const [slides, setSlides] = useState<LmsPreviewSlide[]>([]);
  // What holds a learner back in the app, which the preview itself does not do.
  const [timing, setTiming] = useState<string | null>(null);

  // Back returns to where the admin came from: the editor, or else the content list.
  const fromEditor = (location.state as LmsModulePreviewLocationState | null)?.from === 'editor';
  const backTo = fromEditor && id ? `/lms/content/${id}` : '/lms/content';
  const goBack = () => navigate(backTo);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        // A new load (another module, or the project switched) starts clean: an earlier
        // failure must not stay on screen over a load that works.
        setError(null);
        setLaunchUrl(null);
        const modResp = await lmsAPI.getModuleById(id, projectId);
        const mod = modResp.data?.data?.module;
        if (!active) return;
        const type = mod?.module_type || 'native';
        setModuleType(type);
        if (type === 'scorm') {
          const resp = await lmsAPI.launchScorm(id, projectId);
          const url = resp.data?.data?.launchUrl;
          if (!active) return;
          if (url) setLaunchUrl(url);
          else setError('Could not start the SCORM player.');
        } else {
          setModuleTitle(typeof mod?.title === 'string' ? mod.title : '');
          setModuleStatus(typeof mod?.status === 'string' ? mod.status : null);
          setSlides(previewSlides(modResp.data?.data?.slides));
          setTiming(learnerTiming(mod?.min_time_seconds, mod?.quiz_time_limit_minutes));
        }
      } catch (err: any) {
        if (active) setError(err?.response?.data?.message || 'Failed to load preview');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [id, projectId]);

  if (!loading && !error && moduleType !== 'scorm') {
    const status = moduleStatus ? statusConfig[moduleStatus] : undefined;
    const title = moduleTitle.trim() || 'Untitled module';
    return (
      <div className="space-y-5">
        <div className="space-y-3">
          <button onClick={goBack} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-2xl font-bold text-gray-900 break-words">{title}</h1>
              {status && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>{status.label}</span>
              )}
            </div>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
              <Eye className="w-4 h-4" aria-hidden="true" />
              Preview: nothing is recorded
            </p>
            {timing && <p className="mt-0.5 text-sm text-gray-500">In the app: {timing}</p>}
          </div>
        </div>

        <LmsSlidePreviewFrame
          key={id}
          moduleTitle={title}
          slides={slides}
          onDone={goBack}
          editHref={`/lms/content/${id}`}
        />
      </div>
    );
  }

  // Loading, a failed load, and the SCORM player (as before).
  const scormReady = moduleType === 'scorm' && !loading && !error;
  return (
    <div className="space-y-4">
      <button
        onClick={scormReady ? () => navigate('/lms/content') : goBack}
        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" /> {scormReady ? 'Back to Content' : 'Back'}
      </button>

      {loading ? (
        <BlockLoader className="py-24" />
      ) : error ? (
        <div className="rounded-lg border border-red-100 bg-red-50 p-6 text-sm text-red-700">{error}</div>
      ) : launchUrl ? (
        <div className="rounded-lg border border-gray-200 overflow-hidden bg-black" style={{ height: '80vh' }}>
          <iframe
            title="SCORM preview"
            src={launchUrl}
            className="w-full h-full"
            style={{ border: 0 }}
            allow="autoplay; fullscreen; microphone; camera"
          />
        </div>
      ) : null}
    </div>
  );
}
